
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BreadCrumb from "Common/BreadCrumb";
import callApi from "Common/utils/apiCaller";
import { formatCountdown, getSyncRetrySeconds } from "Common/utils/syncLimit";
import Flatpickr from "react-flatpickr";
import CustomInput from "Common/Components/InputFields/CustomInput";
import CustomSelect from "Common/Components/InputFields/CustomSelect";
import FileUpload from "Common/Components/InputFields/FileUploadBtn";
import IndexCurrency from "../../constants/IndexCurrency.json";
import { showNotification } from "Common/utils/Notification";
import indexValue from "../../constants/indexValue.json";
import constituent from "../../constants/constituent.json";
import checkTypeIndex from "../../constants/checkTypeIndex.json";
import { INDEX_LIST_ROUTE, INDEX_UPDATE_ROUTE } from "constants/routeEnum";
import constituentType from "../../constants/constituentType.json";
import { useParams, useNavigate, Link } from "react-router-dom";
import _, { debounce } from "lodash";
import { Trash2, X } from "lucide-react";
import Drawer from "Common/Components/Drawer";
import LoaderComponent from "Common/Components/Loader/Loader";
import SearchDropdown from "Common/Components/searchComponent/SearchComponent";
import PermissionSwitch from "Common/Components/PermissionLocks/PermissionSwitch";
import { createSelector } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import CustomDateInput from "Common/Components/InputFields/CustomDateInput";
import CustomTextArea from "Common/Components/InputFields/CustomTextArea";
import moment from "moment";
import TableContainer from "Common/TableContainer";
import { PaginationState } from "@tanstack/react-table";
import { getImageUrl } from "Common/utils/ImageDomain";
import ImageView from "Common/Components/ImageView/ImageView";
import { indexmethadologyUploader } from "Common/utils/imageUploader";
import { TailSpin } from "react-loader-spinner";
import MetaData from "Common/MetaData";
import MethodologyFileUploader from "./MethodologyFileUploader";
import IndexDataSource from "./IndexDataSource";
import SourceVerification from "./SourceVerification";

const ConstituentTypes = ["All Constituents", "Only Top 5 Constituents"];

// Languages that add their own name, tagline and description fields.
const EXTRA_LANGUAGES = [
  { key: "portuguese", prefix: "pt", label: "Portuguese" },
  { key: "korean", prefix: "kr", label: "Korean" },
  { key: "hebrew", prefix: "hb", label: "Hebrew" },
  { key: "japanese", prefix: "jp", label: "Japanese" },
];

// A heading that groups the fields below it on the long edit form.
const SectionTitle = ({ title, hint, first }: { title: string; hint?: string; first?: boolean }) => (
  <div className={`col-span-3 ${first ? "" : "pt-5 mt-1 border-t border-dashed border-slate-200 dark:border-zink-500"}`}>
    <h6 className="text-15">{title}</h6>
    {hint && <p className="mt-0.5 text-xs text-slate-500 dark:text-zink-200">{hint}</p>}
  </div>
);

const CheckCaption = ({ text }: { text: string }) => (
  <p className="col-span-3 -mb-3 text-xs font-medium tracking-wide uppercase text-slate-400">{text}</p>
);

const LinearKeys = [
  "page_title",
  "meta_keyword",
  "meta_description",
  "url",
  "canonical_url",
  "name",
  "pt_name",
  "hb_name",
  "jp_name",
  "kr_name",
  "constituents_update",
  "constituents_tagline",
  "pt_constituents_tagline",
  "kr_constituents_tagline",
  "jp_constituents_tagline",
  "hb_constituents_tagline",
  "url",
  "description",
  "pt_description",
  "kr_description",
  "jp_description",
  "hb_description",
  "factsheet_type",
  "factsheet_media_url",
  "factsheet_media_id",
  "methodology_media_url",
  "methodology_media_id",
  "portuguese_media_url",
  "portuguese_media_id",
  "korean_media_url",
  "korean_media_id",
  "hebrew_media_url",
  "hebrew_media_id",
  "japanese_media_url",
  "japanese_media_id",
  "dividend_yield",
  "pr_code",
  "tr_code",
  "ntr_code",
  "benchmark_pr_code",
  "benchmark_tr_code",
  "benchmark_ntr_code",
];

const getLinearKeys = (formData) => {
  return LinearKeys.filter((key) => {
    if (key === "pr_code" && !formData?.return_type?.includes("PR"))
      return false;
    if (key === "tr_code" && !formData?.return_type?.includes("TR"))
      return false;
    if (key === "ntr_code" && !formData?.return_type?.includes("NTR"))
      return false;
    return true;
  });
};

const KeysWithDate = ["base_date"];

const LinearBooleanKeys = [
  "is_factsheet_visible",
  "is_methodology_visible",
  "is_characteristics_visible",
  "is_risk_return_statisics_visible",
  "is_charts_visible",
  "is_download_index_values_visible",
  "is_constituent_visible",
  "is_pr_value_visible",
  "is_tr_value_visible",
  "is_ntr_value_visible",
];

const LineearKeysWithOptions = [
  "factsheet_type",
  "currency",
  "return_type",
  "constituent_type",
  "category",
  "sub_category",
  "constituents_keys_visible",
  "currency",
  "languages",
  "calculator_type",
  "benchmark_pr_code",
  "benchmark_tr_code",
  "benchmark_ntr_code",
];

const ManageIndices = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loader, setLoader] = useState<any>(false);
  const [metaEdit, setMetaEdit] = useState<any>({
    url: "",
    page_title: "",
    meta_description: "",
    meta_keyword: "",
    canonical_url: "",
  });
  const [processLoader, setProcessLoader] = useState<any>(false);
  // Per return type: the date picked for a single-ticker sync and its loading flag.
  const [tickerSync, setTickerSync] = useState<any>({});
  // When the API says a ticker was synced too often, the button waits until this time.
  // Per return type for the ticker buttons, and one for the Sync Data button at the top.
  const [topCooldownUntil, setTopCooldownUntil] = useState<number>(0);
  const [clock, setClock] = useState<number>(Date.now());
  const secondsLeft = (until?: number) =>
    Math.max(0, Math.ceil(((until || 0) - clock) / 1000));
  const anySyncWaiting =
    topCooldownUntil > clock ||
    Object.values(tickerSync).some((state: any) => (state?.until || 0) > clock);

  useEffect(() => {
    if (!anySyncWaiting) return;
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [anySyncWaiting]);
  const [showMeta, setShowMeta] = useState<any>(false);
  const [categoryOption, setCategoryOption] = useState<any>();
  const [subCategoryOption, setSubCategoryOption] = useState<any>();
  const toggleMeta = () => setShowMeta(!showMeta);
  const [docView, setDocView] = useState<any>(undefined);
  const [documentView, setDocumentView] = useState<any>();
  const [showMode, setShowMode] = useState<any>(false);
  const defaultToggle = () => setShowMode(!showMode);
  const [errors, setErrors] = useState<any>({});
  const [checkedType, setCheckedType] = useState<any>([]);
  const [constituentData, setConstituentData] = useState<any>([]);
  const [versionCopy, setVersionCopy] = useState<any>({});
  const [categoryList, setCategoryList] = useState<any>([]);
  const [subCategoryList, setSubCategoryList] = useState<any>([]);
  const [calculatorType, setCalculatorType] = useState<any>([]);
  const [languageList, setLanguageList] = useState<any>([]);
  const [factsheetTypeList, setFactsheetTypeList] = useState<any>([]);
  const [returnTypeList, setReturnTypeList] = useState<any>([]);
  const [constituentTypeList, setConstituentTypeList] = useState<any>([]);
  const [constituentVisibilityList, setConstituentVisibilityList] = useState<any>([]);
  const [indexUrl, setIndexUrl] = useState<string | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [eventData, setEventData] = useState<any>();
  const [totalItems, setTotalItems] = useState(0);
  const [imageData, setImageData] = useState<any>();
  const [selectedFile, setSelectedFile] = useState<any>();
  const [imageLink, setImageLink] = useState<any>("");
  const [indexUploads, setIndexUploads] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<any>(false);
  const [isDocument, setIsDocument] = useState<any>(false);
  const [selectedFiles, setSelectedFiles] = useState<any>();
  const [indexConstituentList, setIndexConstituentList] = useState<any>([]);
  const [showImage, setShowImage] = useState<any>(false);
  const [updatedindexversion, setUpdatedIndexVersion] = useState<any>([]);
  const toggleImage = () => setShowImage(!showImage);

  const selectProperties = createSelector(
    (state: any) => state.Login,
    (profile) => ({
      permission: profile.permission,
      user: profile.user,
    })
  );

  const { permission, user } = useSelector(selectProperties);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 30,
  });

  const initializeFormData = () => {
    const formData = {};
    LinearKeys.forEach((key) => {
      formData[key] = "";
    });
    LinearBooleanKeys.forEach((key) => {
      formData[key] = false;
    });
    LineearKeysWithOptions.forEach((key) => {
      formData[key] = "";
    });
    KeysWithDate.forEach((key) => {
      formData[key] = new Date();
    });
    return formData;
  };

  const [selectedValues, setSelectedValues] = useState<any>([]);

  const [indexVersions, setIndexVersions] = useState<any>({
    PR: {
      index_code: "",
      index_id: "",
      index_name: "",
      benchmark_code: "",
      benchmark_id: "",
      benchmark_name: "",
      is_edit: false,
      updated_index_code: "",
      updated_benchmark_code: "",
      updated_index_name: "",
      updated_benchmark_name: "",
      base_date: new Date().toISOString(),
      updated_base_date: "",
    },
    TR: {
      index_code: "",
      index_id: "",
      index_name: "",
      benchmark_code: "",
      benchmark_id: "",
      benchmark_name: "",
      is_edit: false,
      updated_index_code: "",
      updated_benchmark_code: "",
      updated_index_name: "",
      updated_benchmark_name: "",
      base_date: new Date().toISOString(),
      updated_base_date: "",
    },
    NTR: {
      index_code: "",
      index_id: "",
      index_name: "",
      benchmark_code: "",
      benchmark_id: "",
      benchmark_name: "",
      is_edit: false,
      updated_index_code: "",
      updated_benchmark_code: "",
      updated_index_name: "",
      updated_benchmark_name: "",
      base_date: new Date().toISOString(),
      updated_base_date: "",
    },
  });

  const initialFormData = initializeFormData();

  const [formData, setFormData] = useState<any>(initialFormData);

  const [files, setFiles] = useState<any>({
    factsheet: null,
    indexValues: null,
    constituentsFile: null,
    englishMethodologyFile: null,
    portugeseMethodologyFile: null,
  });

  interface PermissionInfo {
    permissions: string[];
  }

  const rawPermission = localStorage.getItem("permission");
  const premissionInfo: PermissionInfo | null = rawPermission
    ? JSON.parse(rawPermission)
    : null;

  useEffect(() => {
    fetchIndexUploads();
  }, [id]);

  async function fetchIndexUploads() {
    const res = await callApi(`v1/indexupload/list`, "post", {
      pageNum: 1,
      pageSize: 500,
      filters: {
        index_id: id,
      },
    });
    if (res && res?.status === "Success") {
      setIndexUploads(res.data.indexuploadList);
      setTotalItems(res.data.indexuploadCount);
    }
  }

  useEffect(() => {
    const fetchCategory = async () => {
      let filters: { [index: string]: any } = {};

      const res = await callApi("v1/category/list", "post", {
        pageNum: 1,
        pageSize: 100,
        filters,
      });
      if (res?.status === "Success") {
        setCategoryList(
          res.data.categoryList.map((item) => ({
            label: item?.name,
            value: item?.name,
          }))
        );
      }
    };

    fetchCategory();
  }, []);

  // FIXED: Updated subcategory fetch logic
  useEffect(() => {
    const fetchSubCategory = async (code: string) => {
      let filters: { [index: string]: any } = {
        category_slug: _.kebabCase(code),
      };

      const res = await callApi("v1/subcategory/list", "post", {
        pageNum: 1,
        pageSize: 100,
        filters,
      });
      if (res?.status === "Success") {
        const subcategoryOptions = res.data.subcategoryList.map((item) => ({
          label: item?.name,
          value: item?.name,
        }));
        setSubCategoryList(subcategoryOptions);
      }
    };

    // Fetch subcategories when category changes or is set
    if (formData?.category || categoryOption) {
      fetchSubCategory(formData?.category || categoryOption);
    }
  }, [formData?.category, categoryOption]);

  useEffect(() => {
    const fetchDataByCode = async (code: string) => {
      const res = await callApi(`v1/content/code/${code}`, "get", {});
      if (res.status === "Success") {
        return res.data.content?.params || [];
      }
      return [];
    };
    const fetchAllData = async () => {
      let results = await Promise.all([
        fetchDataByCode("factsheet-type"),
        fetchDataByCode("return-type"),
        fetchDataByCode("constituent-type"),
        fetchDataByCode("constituent-visibility"),
        fetchDataByCode("language-type"),
        fetchDataByCode("calculator-type"),
      ]);
      if (results?.length > 0) {
        setFactsheetTypeList(results[0]);
        setReturnTypeList(results[1]);
        setConstituentTypeList(results[2]);
        setConstituentVisibilityList(results[3]);
        setLanguageList(results[4]);
        setCalculatorType(results[5]);
      }
    };

    fetchAllData();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSelectChange = (name: string, selectedValue: string | string[]) => {
    const isOther = selectedValue === "other";
    const isArray = Array.isArray(selectedValue);
    const value = isOther ? "" : selectedValue;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "factsheet_type" && { is_factsheet_visible: true }),
      ...(name === "return_type" && {
        is_constituent_visible:
          isArray && selectedValue[0]?.startsWith("N") ? false : true,
      }),
    }));

    if (name === "category") {
      setCategoryOption(isOther ? "other" : "");
      // Clear subcategory when category changes
      if (!isOther) {
        setFormData((prev) => ({
          ...prev,
          sub_category: "",
        }));
      }
    }

    if (name === "sub_category") {
      setSubCategoryOption(isOther ? "other" : "");
    }

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // FIXED: Updated main data fetch with proper subcategory loading
  useEffect(() => {
    async function fetchData(id: any) {
      try {
        const res = await callApi(`v1/index/${id}/detailed-info`, "get", {});
        if (res.status === "Success") {
          if (res?.data?.index_versions) {
            setUpdatedIndexVersion(res.data.index_versions)
            let versions = res.data.index_versions;
            let newIndexVersions = { ...indexVersions };
            let versionCopyData = {};

            versions.forEach((item) => {
              newIndexVersions[item.return_type] = {
                index_code: item?.index_code ? item?.index_code : "",
                index_id: item?.index_id ? item?.index_id : "",
                index_name: item?.index_name ? item?.index_name : "",
                benchmark_code: item?.benchmark_code
                  ? item?.benchmark_code
                  : "",
                benchmark_id: item?.benchmark_id ? item?.benchmark_id : "",
                benchmark_name: item?.benchmark_name
                  ? item?.benchmark_name
                  : "",
                is_edit: item?.is_edit ? item?.is_edit : false,
                updated_index_code: item?.updated_index_code
                  ? item?.updated_index_code
                  : "",
                updated_benchmark_code: item?.updated_benchmark_code
                  ? item?.updated_benchmark_code
                  : "",
                updated_index_name: item?.updated_index_name
                  ? item?.updated_index_name
                  : "",
                updated_benchmark_name: item?.updated_benchmark_name
                  ? item?.updated_benchmark_name
                  : "",
                base_date: item?.base_date ? item?.base_date : new Date().toISOString(),
                updated_base_date: item?.updated_base_date ? item?.updated_base_date : "",
              };

              versionCopyData[`${item.return_type}_base_date`] = item?.base_date;
            });

            setIndexVersions(newIndexVersions);

            if (res?.data?.index_page) {
              setVersionCopy({
                ...res.data.index_page,
                ...versionCopyData
              });
            }
          }

          setIndexConstituentList(res.data.constituents);

          // Determine which data to use
          const indexPageData = res?.data?.versionCopy || res.data.index_page;

          // FIXED: Fetch subcategories first if category exists
          if (indexPageData?.category) {
            const subCatRes = await callApi("v1/subcategory/list", "post", {
              pageNum: 1,
              pageSize: 100,
              filters: { category_slug: _.kebabCase(indexPageData.category) },
            });

            if (subCatRes?.status === "Success") {
              const subcategoryOptions = subCatRes.data.subcategoryList.map((item) => ({
                label: item?.name,
                value: item?.name,
              }));
              setSubCategoryList(subcategoryOptions);
            }
          }

          // Then set form data
          let selectedValues = indexPageData.return_type;
          setSelectedValues(selectedValues);
          setFormData({
            ...indexPageData,
            description: indexPageData.description || "",
          });
          setMetaEdit(indexPageData);
          setImageLink(indexPageData);
          setSelectedFiles(indexPageData);
          setSelectedFile(indexPageData);
          let arr = [];
          setIndexUrl(indexPageData.url);

          for (let key in indexPageData) {
            if (checkTypeIndex?.some((item) => item.value === key)) {
              if (indexPageData[key]) {
                arr.push(key);
              }
            }
          }
          setCheckedType(arr);
          setConstituentData(indexPageData?.constituents_keys_visible);

          if (!res?.data?.versionCopy) {
            setVersionCopy({});
          }
        }
      } catch (e) {
        showNotification("Error occurred", "error");
      }
    }
    if (id) {
      fetchData(id);
    }
  }, [id]);

  const refetchData = (id: any) => {
    async function fetchData(id: any) {
      try {
        const res = await callApi(`v1/index/${id}/detailed-info`, "get", {});
        if (res.status === "Success") {
          setIndexConstituentList(res.data.constituents);

          const indexPageData = res?.data?.versionCopy || res.data.index_page;

          // Fetch subcategories first if category exists
          if (indexPageData?.category) {
            const subCatRes = await callApi("v1/subcategory/list", "post", {
              pageNum: 1,
              pageSize: 100,
              filters: { category_slug: _.kebabCase(indexPageData.category) },
            });

            if (subCatRes?.status === "Success") {
              const subcategoryOptions = subCatRes.data.subcategoryList.map((item) => ({
                label: item?.name,
                value: item?.name,
              }));
              setSubCategoryList(subcategoryOptions);
            }
          }

          if (res?.data?.versionCopy) {
            setVersionCopy(res.data.index_page);
          } else {
            setVersionCopy({});
          }

          setFormData(indexPageData);
          setImageLink(indexPageData);
          setSelectedFiles(indexPageData);
          setSelectedFile(indexPageData);
          setMetaEdit(indexPageData);
          let arr = [];
          setIndexUrl(indexPageData.url);

          for (let key in indexPageData) {
            if (checkTypeIndex?.some((item) => item.value === key)) {
              if (indexPageData[key]) {
                arr.push(key);
              }
            }
          }
          setCheckedType(arr);
          setConstituentData(indexPageData?.constituents_keys_visible);
        }
      } catch (e) {
        showNotification("Error occurred", "error");
      }
    }
    if (id) {
      fetchData(id);
    }
  };

  const validateForm = () => {
    let newErrors: { [index: string]: any } = {};

    const isEmptyString = (value) =>
      typeof value === "string" && value.trim() === "";

    if (!formData.base_date) newErrors.base_date = "Base Date is required.";


    if (
      !formData.constituents_update ||
      (typeof formData.constituents_update === "string" && formData.constituents_update.trim() === "")
    )
      newErrors.constituents_update = "Constituents Update is required.";


    if (
      !formData.name ||
      (typeof formData.name === "string" && formData.name.trim() === "")
    )
      newErrors.name = "Name is required.";

    if (
      formData.languages?.includes("hebrew") &&
      (!formData.hb_name ||
        (typeof formData.hb_name === "string" && formData.hb_name.trim() === ""))
    ) {
      newErrors.hb_name = "Hebrew Name is required.";
    }

    if (
      formData.languages?.includes("japanese") &&
      (!formData.jp_name ||
        (typeof formData.jp_name === "string" && formData.jp_name.trim() === ""))
    ) {
      newErrors.jp_name = "Japanese Name is required.";
    }

    if (
      formData.languages?.includes("portuguese") &&
      (!formData.pt_name ||
        (typeof formData.pt_name === "string" && formData.pt_name.trim() === ""))
    ) {
      newErrors.pt_name = "Portuguese Name is required.";
    }

    if (
      formData.languages?.includes("korean") &&
      (!formData.kr_name ||
        (typeof formData.kr_name === "string" && formData.kr_name.trim() === ""))
    ) {
      newErrors.kr_name = "Korean Name is required.";
    }


    if (
      !formData.url ||
      (typeof formData.url === "string" && formData.url.trim() === "")
    )
      newErrors.url = "URL is required.";
    if (!formData.category) {
      newErrors.category = "Category is required.";
    }
    if (!formData.currency)
      newErrors.currency = "Currency selection is required.";
    if (
      !formData.description ||
      (typeof formData.description === "string" &&
        formData.description.trim() === "")
    )
      newErrors.description = "Description is required.";
    if (
      formData.languages?.includes("hebrew") &&
      (!formData.hb_description || isEmptyString(formData.hb_description))
    ) {
      newErrors.hb_description = "Hebrew Description is required.";
    }

    if (
      formData.languages?.includes("japanese") &&
      (!formData.jp_description || isEmptyString(formData.jp_description))
    ) {
      newErrors.jp_description = "Japanese Description is required.";
    }

    if (
      formData.languages?.includes("portuguese") &&
      (!formData.pt_description || isEmptyString(formData.pt_description))
    ) {
      newErrors.pt_description = "Portuguese Description is required.";
    }

    if (
      formData.languages?.includes("korean") &&
      (!formData.kr_description || isEmptyString(formData.kr_description))
    ) {
      newErrors.kr_description = "Korean Description is required.";
    }
    if (!formData.factsheet_type)
      newErrors.factsheet_type = "Factsheet Type is required.";
    if (!formData.calculator_type)
      newErrors.calculator_type = "Calculator Type is required.";
    if (
      !formData.constituent_type ||
      (typeof formData.constituent_type === "string" &&
        formData.constituent_type.trim() === "")
    )
      newErrors.constituent_type = "Constituents field is required.";
    if (!formData.languages)
      newErrors.languages = "Language selection is required.";

    if (
      formData.factsheet_type === "static" &&
      !imageLink?.media_url &&
      !imageLink?.factsheet_media_url
    ) {
      newErrors.factsheet =
        "Factsheet file is required for Static Factsheet Type.";
    }
    if (
      formData?.languages?.includes("portuguese") &&
      !formData?.pt_description
    ) {
      newErrors.pt_description = "Portuguese Description is required.";
    }
    if (
      formData?.languages?.includes("korean") &&
      !formData?.kr_description
    ) {
      newErrors.kr_description = "Korean Description is required.";
    }
    if (
      formData?.languages?.includes("hebrew") &&
      !formData?.hb_description
    ) {
      newErrors.hb_description = "Hebrew Description is required.";
    }
    if (
      formData?.languages?.includes("japanese") &&
      !formData?.jp_description
    ) {
      newErrors.jp_description = "Japanese Description is required.";
    }
    if (
      formData?.return_type?.includes("PR") &&
      !indexVersions["PR"]?.index_code &&
      !indexVersions["PR"]?.updated_index_code
    )
      newErrors.pr_code = "PR Ticker is required.";
    if (
      formData?.return_type?.includes("TR") &&
      !indexVersions["TR"]?.index_code &&
      !indexVersions["TR"]?.updated_index_code
    )
      newErrors.tr_code = "TR Ticker is required.";
    if (
      formData?.return_type?.includes("NTR") &&
      !indexVersions["NTR"]?.index_code &&
      !indexVersions["NTR"]?.updated_index_code
    ) {
      newErrors.ntr_code = "NTR Ticker is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleMultiSelectChange = (name: string, selectedValues: string[]) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedValues,
    }));
    setSelectedValues(selectedValues);
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleMetaData = async () => {
    try {
      setLoader(true);
      const res = await callApi(`v1/index/${indexUrl}/update-indxx`, "post", {
        ...metaEdit,
      });
      setLoader(false);
      if (res && res?.status === "Success") {
        showNotification("Updated successfully", "success");
        refetchData(id);
        toggleMeta();
      }
    } catch (e) {
      showNotification(
        e.response.data.data.message || "Error occured",
        "error"
      );
      setLoader(false);
    }
  };

  const showError = (keyName) => {
    return errors && errors[keyName] ? (
      <p className="text-red-500 text-[14px]  pt-2">{errors[keyName]}</p>
    ) : null;
  };

  const handleMetaChange = (keyName, keyValue) => {
    setMetaEdit((prev) => ({
      ...prev,
      [`updated_${keyName}`]: keyValue,
      is_meta_edited: true,
    }));
    setErrors((prev) => ({
      ...prev,
      [`updated_${keyName}`]: "",
    }));
  };

  const handleDownloadConstituentSample = async () => {
    let arr = [];

    const rows = [["constituent", "isin", "weight", "cdate", "mic"]];

    let csvContent = "";

    constituent?.forEach((e) => {
      rows.push([e.constituent, e.isin, e.weight, e.cdate, e.mic]);
    });

    rows.forEach((rowArray) => {
      let row = rowArray.join(",");
      csvContent += row + "\n";
    });
    let contentType = "text/csv";
    let filename = `${`contituent_sample`}.csv`;

    if (!contentType) contentType = "application/octet-stream";
    var a = document.createElement("a");
    var blob = new Blob([csvContent], { type: contentType });
    a.href = window.URL.createObjectURL(blob);
    a.download = filename;
    a.click();
  };

  const downloadConstituentSample = () => {
    try {
      handleDownloadConstituentSample();
      toast.success("File downloaded 👌", { hideProgressBar: true });
    } catch (error) {
      toast.error("Failed to download 🤯", { hideProgressBar: true });
    }
  };

  const downloadValueSample = () => {
    try {
      handleDownloadValueSample();
      toast.success("File downloaded 👌", { hideProgressBar: true });
    } catch (error) {
      toast.error("Failed to download 🤯", { hideProgressBar: true });
    }
  };

  const handleFormData = (e) => {
    const { name, value } = e.target;
    let update = {
      ...formData,
    };
    update[name] = !update[name];
    setFormData(update);
    let arr = [];
    if (!checkedType.some((existingItem) => existingItem === name)) {
      arr.push(name);
    }
    setCheckedType((prev) => {
      if (prev.some((lead) => lead === name)) {
        return prev.filter((item) => !(item === name));
      }

      return [...prev, ...arr];
    });
  };

  const handleConstituentData = (e) => {
    const { name, value } = e.target;

    let arr = [];
    if (!constituentData.some((existingItem) => existingItem === name)) {
      arr.push(name);
    }
    setConstituentData((prev) => {
      if (prev.some((lead) => lead === name)) {
        return prev.filter((item) => !(item === name));
      }

      return [...prev, ...arr];
    });
  };

  useEffect(() => {
    if (checkedType?.length !== 0) {
      for (let item of checkedType) {
        setFormData((prev) => ({
          ...prev,
          [item]: true,
        }));
      }
      for (let item of checkTypeIndex) {
        if (!checkedType?.some((items) => items === item.label)) {
          setFormData((prev) => ({
            ...prev,
            [item.label]: false,
          }));
        }
      }
    } else {
      for (let item of checkTypeIndex) {
        setFormData((prev) => ({
          ...prev,
          [item.label]: false,
        }));
      }
    }
  }, [checkedType]);

  useEffect(() => {
    if (constituentData?.length !== 0) {
      setFormData((prev) => ({
        ...prev,
        ["constituents_keys_visible"]: constituentData,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        ["constituents_keys_visible"]: [],
      }));
    }
  }, [constituentData]);

  const handleDownloadValueSample = async () => {
    let arr = [];

    const rows = [["date", "pr_value", "tr_value", "ntr_value"]];

    let csvContent = "";

    indexValue?.forEach((e) => {
      rows.push([e.date, e["pr_value"], e["tr_value"], e["ntr_value"]]);
    });

    rows.forEach((rowArray) => {
      let row = rowArray.join(",");
      csvContent += row + "\n";
    });
    let contentType = "text/csv";
    let filename = `${`value_sample`}.csv`;

    if (!contentType) contentType = "application/octet-stream";
    var a = document.createElement("a");
    var blob = new Blob([csvContent], { type: contentType });
    a.href = window.URL.createObjectURL(blob);
    a.download = filename;
    a.click();
  };

  useEffect(() => {
    if (imageData && imageData?.media_url) {
      if (imageData?.media_type === "document") {
        setImageLink(imageData);
      } else {
        showNotification("Please select pdf", "error");
      }
    }
  }, [imageData]);

  // useEffect(() => {
  //   if (formData.base_date) {
  //     setIndexVersions((prev) => {
  //       const updated = { ...prev };

  //       if (!versionCopy || !versionCopy.base_date) {
  //         Object.keys(updated).forEach((returnType) => {
  //           updated[returnType] = {
  //             ...updated[returnType],
  //             base_date: formData.base_date,
  //           };
  //         });
  //       } else {
  //         Object.keys(updated).forEach((returnType) => {
  //           updated[returnType] = {
  //             ...updated[returnType],
  //             updated_base_date: formData.base_date,
  //           };
  //         });
  //       }

  //       return updated;
  //     });
  //   }
  // }, [formData.base_date, versionCopy]);

  const handleAcceptedFiles = async (file: any, type: string) => {
    if (file?.type?.split("/")[1] === "pdf") {
      setIsLoading(true);
      const res = await indexmethadologyUploader(file, id);
      setIsLoading(false);
      if (res.status === "Success") {
        showNotification("Uploaded successfully", "success");
        if (type === "methodology") {
          let update = {
            ...formData,
            [`${type}_media_url`]: res.data.fileData.file_url,
            [`${type}_media_id`]: res.data.fileData.file_id,
          };
          setFormData(update);
        } else {
          let update = {
            ...formData,
            [`${type}_media_url`]: res.data.fileData.file_url,
            [`${type}_media_id`]: res.data.fileData.file_id,
          };
          setFormData(update);
        }
        setSelectedFile(res.data.fileData);
      } else {
        setIsLoading(false);
      }
    } else {
      showNotification("File type should be pdf", "error");
    }
  };

  const handleAcceptedFile = async (file: any, type: String) => {
    if (file?.type?.split("/")[1] === "pdf") {
      setIsDocument(true);
      const res = await indexmethadologyUploader(file, id);
      setIsDocument(false);
      if (res.status === "Success") {
        showNotification("Uploaded successfully", "success");
        setSelectedFiles(res.data.fileData);
        let update = {
          ...formData,
          [`${type}_media_url`]: res.data.fileData.file_url,
          [`${type}_media_id`]: res.data.fileData.file_id,
        };
        setFormData(update);
        setIsDocument(false);
      } else {
        setIsDocument(false);
      }
    } else {
      showNotification("File type should be pdf", "error");
    }
  };

  const handleAcceptedFileUpload = async (file: any, type: string) => {
    if (file?.type?.split("/")[1] === "pdf") {
      setIsDocument(true);

      const res = await indexmethadologyUploader(file, id);
      setIsDocument(false);

      if (res.status === "Success") {
        showNotification("Uploaded successfully", "success");

        setSelectedFiles(res.data.fileData);
        setImageLink(res.data.fileData);

        setFormData((prev) => ({
          ...prev,
          factsheet_media_url: res.data.fileData.media_url,
          factsheet_media_type: "pdf",
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    if (!validateForm()) {
      console.warn("Validation failed:", errors);
      showNotification("Please fix the validation errors.", "error");
      console.warn("Validation failed:", errors);
      return;
    }

    let payload = {};
    const filteredLinearKeys = getLinearKeys(formData);
    for (let key of filteredLinearKeys) {
      payload[key] = formData[key] || "";
    }
    for (let key of LinearBooleanKeys) {
      console.log(key, formData[key]);
      payload[key] = formData[key] ? true : false;
    }
    for (let key of LineearKeysWithOptions) {
      payload[key] =
        _.isNull(formData[key]) || _.isUndefined(formData[key])
          ? null
          : formData[key];
    }
    for (let key of KeysWithDate) {
      payload[key] = formData[key] || new Date()?.toISOString();
    }

    payload["factsheet_media_url"] = imageLink?.media_url
      ? imageLink?.media_url
      : null;

    let versions = [];
    for (let key of formData?.return_type) {
      versions.push({
        ...indexVersions[key],
        return_type: key,
        base_date: indexVersions[key]?.base_date || formData.base_date,
      });
    }
    payload["versions"] = versions.map((item) => {
      return {
        ...item,
        index_code: item?.updated_index_code
          ? item.updated_index_code
          : item?.index_code,
        benchmark_code: item?.updated_benchmark_code
          ? item.updated_benchmark_code
          : item?.benchmark_code,
        index_name: item.updated_index_name
          ? item.updated_index_name
          : item.index_name,
        benchmark_name: item.updated_benchmark_name
          ? item.updated_benchmark_name
          : item.benchmark_name,
        base_date: item.updated_base_date
          ? item.updated_base_date
          : item.base_date,
      };
    });

    const validIndexIds = new Set(versions.map((v) => v.return_type));

    payload["index_versions"] = updatedindexversion.filter((iv) => validIndexIds.has(iv.return_type)).map((item) => item._id);

    payload["is_constituent_visible"] = payload["constituent_type"] == "No Constituents" ? false : true;

    console.log(payload)

    try {
      setLoader(true);
      const res = await callApi(
        `v1/index/${indexUrl}/update-indxx`,
        "post",
        payload
      );
      setLoader(false);
      if (res.status === "Success") {
        refetchData(id);
        showNotification("Index updated successfully", "success");
        fetchIndexUploads();
      }
    } catch (e) {
      console.error("API Error:", e);
      showNotification(
        e.response?.data?.data?.message || "Error occurred",
        "error"
      );
      setLoader(false);
    }
  };

  const handleDummy = (e) => {
    console.log(e);
  };

  const handlePublish = async () => {
    if (!indexUrl) {
      showNotification("Invalid URL", "error");
      return;
    }

    try {
      setLoader(true);
      const res = await callApi(`v1/index/url/${indexUrl}/publish`, "get");
      setLoader(false);
      if (res.status === "Success") {
        refetchData(id);
        showNotification("Index published successfully", "success");
      } else {
        showNotification(res.data?.message || "Publishing failed", "error");
      }
    } catch (e) {
      console.error("API Error:", e);
      showNotification(e.response?.data?.message || "Error occurred", "error");
      setLoader(false);
    }
  };

  const handleApprove = async () => {
    if (!indexUrl) {
      showNotification("Invalid URL", "error");
      return;
    }

    try {
      setLoader(true);
      const res = await callApi(`v1/index/url/${indexUrl}/approve`, "get");
      setLoader(false);

      if (res.status === "Success") {
        refetchData(id);
        showNotification("Index approved successfully", "success");
      } else {
        showNotification(res.data?.message || "Approving failed", "error");
      }
    } catch (e) {
      console.error("API Error:", e);
      showNotification(e.response?.data?.message || "Error occurred", "error");
      setLoader(false);
    }
  };

  const handleProcessCalculcations = async () => {
    try {
      setProcessLoader(true);

      const response1 = await callApi(
        `v1/index/${id}/update-daily-return-value`,
        "post",
        {}
      );

      if (response1?.status === "Success") {
        showNotification(
          "Daily return value processed successfully",
          "success"
        );
      }

      const response2 = await callApi(
        `v1/index/${id}/update-index-calculations`,
        "post",
        {}
      );

      if (response2?.status === "Success") {
        showNotification("Index calculation processed successfully", "success");
      }
    } catch (error) {
      showNotification(
        error?.response?.data?.data?.message || "An error occurred",
        "error"
      );
    } finally {
      setProcessLoader(false);
    }
  };

  const downloadCSV = (csvString, filename) => {
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadReturnType = async (csvString: string, filename: string) => {
    await new Promise<void>((resolve, reject) => {
      try {
        downloadCSV(csvString, filename);
        showNotification("Downloaded Successfully", "success");
        resolve();
      } catch (error) {
        showNotification("Download failed", "error");
        reject(error);
      }
    });
  };

  const handleReturnTypeValueDownload = async (returnType, id) => {
    try {
      const res = await callApi(
        `v1/index/${id}/download-index-data?return_type=${returnType}`,
        "post",
        {}
      );

      if (typeof res !== "string") {
        showNotification("Invalid CSV data received", "error");
        return;
      }
      let filename = indexVersions[returnType.toUpperCase()]?.index_code;
      console.log(filename, "filename");
      await downloadReturnType(res, filename);
    } catch (e) {
      showNotification("Error occurred", "error");
    }
  };

  const handleValueDownload = async () => {
    const indexCodes = (Object.values(indexVersions || {}) as { index_code: string }[])
      .map((v) => v.index_code)
      .filter(Boolean);

    if (indexCodes.length === 0) {
      showNotification("No index codes found", "error");
      return;
    }

    try {
      const allDataRows: string[] = [];
      const header = "index_code,index_date,value";
      let filename: string | null = null;

      for (const index_code of indexCodes) {
        const response = await fetch(`https://prod-api.indxx.com/api/v1/indexvalue/${index_code}/values`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        const json = await response.json();
        const csvText = json?.data?.csv || "";

        if (!filename && json?.data?.filename) {
          filename = json.data.filename;
        }

        if (!csvText || !csvText.includes(",")) {
          console.warn(`No valid CSV data for index: ${index_code}`);
          continue;
        }

        const lines = csvText.trim().split("\n");
        if (lines.length <= 1) continue;

        const dataRows = lines.slice(1);
        dataRows.forEach((line) => {
          const [date, value] = line.split(",");
          if (!date || !value || isNaN(parseFloat(value))) return;
          allDataRows.push(`${index_code},${date.replace(/"/g, "")},${parseFloat(value)}`);
        });
      }

      if (allDataRows.length === 0) {
        showNotification("No index values available to download", "error");
        return;
      }

      const mergedCSV = [header, ...allDataRows].join("\n");
      const blob = new Blob([mergedCSV], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "all-index-values.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showNotification("Index values downloaded successfully", "success");
    } catch (error) {
      console.error("Download error:", error);
      showNotification("An error occurred while downloading the file", "error");
    }
  };

  // Last weekday before today, the same default the API uses when no date is sent.
  const defaultTickerSyncDate = () => {
    const d = moment().subtract(1, "day");
    while (d.day() === 0 || d.day() === 6) d.subtract(1, "day");
    return d.format("YYYY-MM-DD");
  };

  // Syncs one ticker's value for a single day from its data source.
  const handleTickerSync = async (returnType: string) => {
    const ticker = indexVersions[returnType]?.index_code;
    const date = tickerSync[returnType]?.date || defaultTickerSyncDate();

    if (!ticker) {
      showNotification(`Save a ${returnType} ticker before syncing`, "error");
      return;
    }
    if (moment(date).isAfter(moment(), "day") || [0, 6].includes(moment(date).day())) {
      showNotification("Pick a past weekday", "error");
      return;
    }
    if (
      !window.confirm(
        `Sync ${ticker} for ${date} from its data source? This replaces the stored value for that day.`
      )
    ) {
      return;
    }

    setTickerSync((prev) => ({ ...prev, [returnType]: { ...prev[returnType], loading: true } }));
    try {
      const res = await callApi(
        `v1/indexvalue/${encodeURIComponent(ticker)}/sync-value?date=${date}`,
        "get"
      );
      if (res?.status === "Success") {
        showNotification(`${ticker} synced for ${date}`, "success");
      } else {
        showNotification(res?.data?.message || "Sync failed", "error");
      }
    } catch (error: any) {
      const wait = getSyncRetrySeconds(error);
      if (wait) {
        // Synced too often: keep the button off until the API says it is allowed again.
        setTickerSync((prev) => ({
          ...prev,
          [returnType]: { ...prev[returnType], until: Date.now() + wait * 1000 },
        }));
        setClock(Date.now());
        showNotification(
          error?.message || `Too many syncs for ${ticker}. Try again later.`,
          "warning"
        );
      } else {
        showNotification(error?.message || "Sync failed", "error");
      }
    } finally {
      setTickerSync((prev) => ({ ...prev, [returnType]: { ...prev[returnType], loading: false } }));
    }
  };

  const handleSyncData = async () => {
    try {
      if (!formData?.return_type || formData.return_type.length === 0) {
        showNotification("No return type selected", "error");
        return;
      }

      const firstReturnType = formData.return_type[0];
      const versionData = indexVersions[firstReturnType];

      const indexCode =
        versionData?.updated_index_code || versionData?.index_code;

      if (!indexCode) {
        showNotification("Index code not found", "error");
        return;
      }

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 7);

      const formatDate = (date) => date.toISOString().split("T")[0];
      const start = formatDate(startDate);
      const end = formatDate(endDate);

      const url = `v1/indexvalue/sync-index-value?index_code=${indexCode}&start_date=${start}&end_date=${end}`;

      const res = await callApi(url, "post", {});

      showNotification("Data synced successfully", "success");
      console.log("Sync response:", res);
    } catch (error: any) {
      const wait = getSyncRetrySeconds(error);
      if (wait) {
        setTopCooldownUntil(Date.now() + wait * 1000);
        setClock(Date.now());
        showNotification(
          error?.message || "Too many syncs for this index. Try again later.",
          "warning"
        );
        return;
      }
      console.error("Sync error", error);
      showNotification("Failed to sync data", "error");
    }
  };

  const indexColumns = useMemo(
    () => [
      {
        header: "File Name",
        accessorKey: "file_name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {cell.row.original?.file_name}
              </span>
              <button
                onClick={() =>
                  window.open(
                    getImageUrl(cell.row.original?.file_url),
                    "_blank"
                  )
                }
                className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download
              </button>
            </div>
          </>
        ),
      },
      {
        header: "Uploaded By",
        accessorKey: "uploaded_by",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => cell.row.original?.uploaded_by?.contact_name,
      },
      {
        header: "Uploaded At",
        accessorKey: "created_at",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) =>
          moment(cell.row.original?.created_at).format("DD MMM YY"),
      },

      {
        header: "Model Type",
        accessorKey: "model_type",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Upload Status",
        accessorKey: "is_processed",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) =>
          cell.row.original?.is_processed ? "Completed" : "Not Completed",
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => <div className="flex gap-3"></div>,
      },
    ],
    []
  );

  const handleVersionChange = (
    returnType: string,
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    setIndexVersions((prev) => ({
      ...prev,
      [returnType]: {
        ...prev[returnType],
        [name]: value,
      },
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleBenchmarkChange = (return_type, name, value) => {
    setIndexVersions((prev) => ({
      ...prev,
      [return_type]: {
        ...prev[return_type],
        [name]: value ? value.value : "",
      },
    }));
  };

  const handleUpdate = (data, key) => {
    console.log(data, "data");
    let update = {
      ...formData,
      [key]: data?.file_url,
    };
    if (key === "methodology_media_url") {
      update.methodology_media_id = data?._id;
    } else if (key === "portuguese_media_url") {
      update.portuguese_media_id = data?._id;
    }
    else if (key === "korean_media_url") {
      update.korean_media_id = data?._id;
    }
    else if (key === "hebrew_media_url") {
      update.hebrew_media_id = data?._id;
    }
    else if (key === "japanese_media_url") {
      update.japanese_media_id = data?._id;
    }
    setFormData(update);
  };

  const Constituentsupdate = [
    { label: "No Change", value: "nochange" },
    { label: "Daily", value: "daily" },
    { label: "1", value: "1" },
    { label: "2", value: "2" },
    { label: "3", value: "3" },
    { label: "4", value: "4" },
    { label: "5", value: "5" },
    { label: "6", value: "6" },
    { label: "7", value: "7" },
    { label: "8", value: "8" },
    { label: "9", value: "9" },
    { label: "10", value: "10" },
    { label: "11", value: "11" },
    { label: "12", value: "12" },
    { label: "13", value: "13" },
    { label: "14", value: "14" },
    { label: "15", value: "15" },
    { label: "16", value: "16" },
    { label: "17", value: "17" },
    { label: "18", value: "18" },
    { label: "19", value: "19" },
    { label: "20", value: "20" },
    { label: "21", value: "21" },
    { label: "22", value: "22" },
    { label: "23", value: "23" },
    { label: "24", value: "24" },
    { label: "25", value: "25" },
    { label: "26", value: "26" },
    { label: "27", value: "27" },
    { label: "28", value: "28" },
    { label: "29", value: "29" },
    { label: "30", value: "30" },
    { label: "31", value: "31" }
    
  ];


  console.log(indexVersions, "indexVersions");
  console.log(documentView, "documentView");

  return (
    <>
      <React.Fragment>
        <div className="flex justify-between mt-2">
          <BreadCrumb
            title={INDEX_UPDATE_ROUTE}
            pageTitle={INDEX_LIST_ROUTE}
            version="v1"
          />
          <div className="flex items-center">
            <button
              onClick={handleSyncData}
              type="button"
              disabled={secondsLeft(topCooldownUntil) > 0}
              title={
                secondsLeft(topCooldownUntil) > 0
                  ? "Synced too often. Try again when the timer ends."
                  : undefined
              }
              className="mr-4 text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="align-middle">
                {secondsLeft(topCooldownUntil) > 0
                  ? `Wait ${formatCountdown(secondsLeft(topCooldownUntil))}`
                  : "Sync Data"}
              </span>
            </button>

            <button
              type="button"
              className={`mr-2  text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20`}
              onClick={() => {
                handleProcessCalculcations();
              }}
            >
              <span className="align-middle">
                {processLoader ? (
                  <div className=" w-[100px] flex justify-center">
                    <LoaderComponent />{" "}
                  </div>
                ) : (
                  "Process Calculations"
                )}
              </span>
            </button>

            {formData &&
              versionCopy &&
              versionCopy.is_edit &&
              !formData.is_approved &&
              !formData.is_published ? (
              <PermissionSwitch
                hasPermissions={premissionInfo?.permissions ?? []}
                shouldHavePermissions={["index:verify"]}
                yes={
                  <button
                    type="button"
                    className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    onClick={() => {
                      handleApprove();
                    }}
                  >
                    <span className="align-middle">Approve</span>
                  </button>
                }
                no={<span></span>}
              />
            ) : null}
            {formData &&
              versionCopy &&
              versionCopy.is_edit &&
              formData.is_approved &&
              !formData.is_published ? (
              <PermissionSwitch
                hasPermissions={premissionInfo?.permissions ?? []}
                shouldHavePermissions={["index:publish"]}
                yes={
                  <button
                    type="button"
                    className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    onClick={() => {
                      handlePublish();
                    }}
                  >
                    <span className="align-middle">Publish</span>
                  </button>
                }
                no={<span></span>}
              />
            ) : null}
            <button
              type="button"
              className="ml-2 text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              onClick={() => toggleMeta()}
            >
              <span className="align-middle">Edit Meta</span>
            </button>
          </div>
        </div>
        <ToastContainer closeButton={false} limit={1} />
        <div className="grid grid-cols-12 gap-x-5">
          <div className="col-span-12 card 2xl:col-span-12">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <h4 className="text-15">Manage Indices</h4>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    className="flex items-center px-4 py-2 space-x-2 border border-gray-300 rounded-md hover:bg-gray-100"
                    onClick={() => downloadConstituentSample()}
                  >
                    ⬇️ <span>Constituent Sample File</span>
                  </button>

                  <button
                    type="button"
                    className="flex items-center px-4 py-2 space-x-2 border border-gray-300 rounded-md hover:bg-gray-100"
                    onClick={() => downloadValueSample()}
                  >
                    ⬇️ <span>Index Value Sample File</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="!py-3.5 card-body border-y border-dashed border-slate-200 dark:border-zink-500">
              <form className="grid grid-cols-3 mx-auto gap-x-8 gap-y-5 max-w-7xl">
                <SectionTitle first title="Basics" hint="Base date, address and category of the index." />
                <div>
                  <CustomDateInput
                    value={formData.base_date}
                    dateOptions={{ dateFormat: "Y-m-d" }}
                    isRequired={true}
                    keyName="base_date"
                    label="Base Date"
                    setDate={(selectedDates) => {
                      setErrors((prev) => ({ ...prev, ["base_date"]: "" }));
                      setFormData((prev) => ({
                        ...prev,
                        base_date:
                          selectedDates && selectedDates[0]
                            ? selectedDates[0]?.toISOString()
                            : new Date().toISOString(),
                      }));
                    }}
                    isVersioned={!!versionCopy?.base_date}
                    versionedValue={moment(
                      new Date(versionCopy?.base_date)
                    ).toISOString()}
                    placeholder="Select Date"
                    errors={errors}
                  />
                </div>



                <CustomInput
                  label="Url"
                  name="url"
                  type="text"
                  value={formData.url}
                  onChange={handleInputChange}
                  error={errors.url}
                  required
                  placeholder="Enter Url"
                  isVersioned={!!versionCopy?.url}
                  versionedValue={versionCopy?.url}
                  readOnly={true}
                />
                <div className="col-span-1">
                  <CustomSelect
                    label="Category"
                    name="category"
                    value={categoryOption || formData.category}
                    onChange={(selectedValue) =>
                      handleSelectChange("category", selectedValue)
                    }
                    isSearchable={true}
                    options={categoryList}
                    error={errors.category}
                    required
                    isVersioned={!!versionCopy?.category}
                    versionedValue={versionCopy?.category}
                  />
                  {categoryOption === "other" ? (
                    <CustomInput
                      label="Other"
                      name="category"
                      type="text"
                      value={formData.category}
                      onChange={handleInputChange}
                      error={""}
                      required={false}
                      placeholder="Enter other category"
                    />
                  ) : null}
                </div>
                <div className="col-span-1">
                  <CustomSelect
                    label="Sub Category"
                    name="sub_category"
                    value={subCategoryOption || formData.sub_category}
                    onChange={(selectedValue) =>
                      handleSelectChange("sub_category", selectedValue)
                    }
                    options={subCategoryList}
                    isSearchable={true}
                    isVersioned={!!versionCopy?.sub_category}
                    versionedValue={versionCopy?.sub_category}
                    error={""}
                  />
                  {subCategoryOption === "other" ? (
                    <CustomInput
                      label="Other"
                      name="sub_category"
                      type="text"
                      value={formData.sub_category}
                      onChange={handleInputChange}
                      error={""}
                      required={false}
                      placeholder="Enter other sub category"
                    />
                  ) : null}
                </div>

                <CustomSelect
                  label="Currency"
                  name="currency"
                  value={formData.currency}
                  onChange={(selectedValue) =>
                    handleSelectChange("currency", selectedValue)
                  }
                  options={IndexCurrency}
                  error={errors.currency}
                  required
                  isSearchable={true}
                  isVersioned={!!versionCopy?.currency}
                  versionedValue={versionCopy?.currency}
                />


                <div className="col-span-1">
                  <CustomSelect
                    label="Constituents Update"
                    name="constituents_update"
                    value={formData.constituents_update}
                    onChange={(selectedValue) => {
                      setFormData(prev => ({ ...prev, constituents_update: selectedValue }));
                      setErrors(prev => ({ ...prev, constituents_update: '' }));
                    }}
                    options={Constituentsupdate}
                    error={errors.constituents_update}
                    required
                    isSearchable={false}
                    isVersioned={!!versionCopy?.constituents_update}
                    versionedValue={versionCopy?.constituents_update}
                  />
                </div>


                <SectionTitle title="Name and description" hint="What visitors read on the index page." />
                <div className="col-span-2">
                  <CustomInput
                    label="Name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    error={errors.name}
                    required
                    placeholder="Enter Name"
                    isVersioned={!!versionCopy?.name}
                    versionedValue={versionCopy?.name}
                  />
                </div>

                <div className="col-span-1">
                  <CustomInput
                    label="Constituents Tagline"
                    name="constituents_tagline"
                    type="text"
                    value={formData.constituents_tagline}
                    onChange={handleInputChange}
                    error={errors.constituents_tagline}
                    // required
                    placeholder="Enter Constituents Tagline"
                    isVersioned={!!versionCopy?.constituents_tagline}
                    versionedValue={versionCopy?.constituents_tagline}
                  />
                </div>


                {EXTRA_LANGUAGES.map(({ key, prefix, label }) =>
                  formData?.languages?.includes(key) ? (
                    <React.Fragment key={key}>
                      <div className="col-span-2">
                        <CustomInput
                          label={`${label} Name`}
                          name={`${prefix}_name`}
                          type="text"
                          value={formData[`${prefix}_name`]}
                          onChange={handleInputChange}
                          error={errors[`${prefix}_name`]}
                          required
                          placeholder="Enter Name"
                          isVersioned={!!versionCopy?.[`${prefix}_name`]}
                          versionedValue={versionCopy?.[`${prefix}_name`]}
                        />
                      </div>
                      <div className="col-span-1">
                        <CustomInput
                          label={`${label} Constituents Tagline`}
                          name={`${prefix}_constituents_tagline`}
                          type="text"
                          value={formData[`${prefix}_constituents_tagline`]}
                          onChange={handleInputChange}
                          error={errors[`${prefix}_constituents_tagline`]}
                          placeholder={`Enter ${label} Constituents Tagline`}
                          isVersioned={!!versionCopy?.[`${prefix}_constituents_tagline`]}
                          versionedValue={versionCopy?.[`${prefix}_constituents_tagline`]}
                        />
                      </div>
                    </React.Fragment>
                  ) : null
                )}


                <div className="col-span-2">
                  <CustomTextArea
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={(value) => {
                      setFormData((prev) => ({
                        ...prev,
                        description: value,
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        description: "",
                      }));
                    }}
                    error={errors.description}
                    required
                    isVersioned={!!versionCopy?.description}
                    versionedValue={versionCopy?.description}
                  />
                </div>

                {EXTRA_LANGUAGES.map(({ key, prefix, label }) =>
                  formData?.languages?.includes(key) ? (
                    <div className="col-span-2" key={key}>
                      <CustomTextArea
                        label={`${label} Description`}
                        name={`${prefix}_description`}
                        value={formData[`${prefix}_description`]}
                        onChange={(value) => {
                          setFormData((prev) => ({ ...prev, [`${prefix}_description`]: value }));
                          setErrors((prev) => ({ ...prev, [`${prefix}_description`]: "" }));
                        }}
                        error={errors[`${prefix}_description`]}
                        required
                        isVersioned={!!versionCopy?.[`${prefix}_description`]}
                        versionedValue={versionCopy?.[`${prefix}_description`]}
                      />
                    </div>
                  ) : null
                )}

                <div className="col-span-1"></div>

                <SectionTitle title="Factsheet" />
                <CustomSelect
                  label="Factsheet Type"
                  name="factsheet_type"
                  value={formData.factsheet_type}
                  onChange={(selectedValue) =>
                    handleSelectChange("factsheet_type", selectedValue)
                  }
                  options={factsheetTypeList}
                  error={errors.factsheet_type}
                  required
                  isVersioned={!!versionCopy?.factsheet_type}
                  versionedValue={versionCopy?.factsheet_type}
                />

                {formData?.factsheet_type === "static" && (
                  formData?.factsheet_media_url || imageLink?.media_url ? (
                    <div className="col-span-1">
                      <label className="block mb-1 font-medium">Factsheet</label>
                      <div className="flex gap-2">
                        <div
                          onClick={() => {
                            setImageData(undefined);
                            toggleImage();
                          }}
                          className="flex items-center px-4 py-2 space-x-2 border border-gray-300 rounded-md hover:bg-gray-100"
                        >
                          📂 Change Factsheet
                        </div>

                        <div
                          onClick={() => {
                            let document = imageLink?.media_url || formData?.factsheet_media_url;
                            setDocumentView({
                              document,
                              file_name: imageLink?.file_name ?? "factsheet.pdf",
                            });
                            setShowMode(true);
                            setDocView(undefined);

                          }}
                          className="flex items-center px-4 py-2 space-x-2 border border-gray-300 rounded-md hover:bg-gray-100"
                        >
                          View
                        </div>
                      </div>
                      <div className="w-4/5 mt-2 overflow-hidden">
                        {imageLink?.file_name || formData?.factsheet_media_url?.split("/").pop()}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="col-span-1">
                        <label className="block mb-1 font-medium">
                          {"Factsheet"}
                        </label>
                        <div
                          onClick={() => {
                            setImageData(undefined);
                            toggleImage();
                          }}
                          className="flex items-center px-4 py-2 space-x-2 border border-gray-300 rounded-md hover:bg-gray-100"
                        >
                          {"⬆️ Add Factsheet"}
                        </div>
                      </div>
                    </>
                  )
                )}

                <SectionTitle title="Tickers and data sources" hint="One block per return type. Each ticker has its own data source." />
                <CustomSelect
                  label="Return Type"
                  name="return_type"
                  value={selectedValues}
                  onChange={(selectedValues) =>
                    handleMultiSelectChange(
                      "return_type",
                      selectedValues as string[]
                    )
                  }
                  options={returnTypeList}
                  error={errors.return_type}
                  required
                  isMulti
                  isVersioned={!!versionCopy?.return_type}
                  versionedValue={
                    versionCopy?.return_type?.length > 0
                      ? versionCopy?.return_type.join(",")
                      : null
                  }
                />

                {formData?.factsheet_type !== "static" && (
                  <div className="col-span-1"></div>
                )}

                {selectedValues &&
                  selectedValues?.map((item) => (
                    <div className="col-span-3" key={item}>
                      <div className="grid grid-cols-4 gap-x-4">
                        <div className="col-span-1">
                          <CustomInput
                            label="Return Type"
                            name=""
                            type="text"
                            value={item}
                            onChange={handleDummy}
                            error={""}
                            placeholder=""
                            required
                            disabled
                            isVersioned={!!versionCopy?.return_type}
                            versionedValue={item}
                          />
                        </div>
                        <div className="col-span-1">
                          <CustomInput
                            label="Ticker"
                            name={
                              !!versionCopy?.return_type
                                ? "updated_index_code"
                                : "index_code"
                            }
                            type="text"
                            value={
                              !!versionCopy?.return_type
                                ? item && indexVersions[item]?.updated_index_code
                                  ? indexVersions[item].updated_index_code
                                  : indexVersions[item]?.index_code
                                : indexVersions[item]?.index_code
                            }
                            onChange={(e) => handleVersionChange(item, e)}
                            error={item?.includes("PR") ? errors.pr_code : ""}
                            placeholder="Enter Ticker Code"
                            required
                            isVersioned={!!versionCopy?.return_type}
                            versionedValue={indexVersions[item]?.index_code}
                          />
                        </div>
                        <div className="col-span-1">
                          <label className="block mb-1 font-medium">
                            {"Benchmark"}
                          </label>
                          {!!versionCopy?.return_type ? (
                            <div className="relative mb-2">
                              <div
                                className={`form-select w-full border rounded-lg p-2 bg-white ${false ? "border-red-500" : "border-gray-300"
                                  } focus:outline-none focus:ring-2 focus:ring-blue-500 ${true
                                    ? "cursor-not-allowed opacity-80"
                                    : "cursor-pointer"
                                  }`}
                                onClick={() => { }}
                              >
                                {indexVersions[item]?.benchmark_code ||
                                  "Select Benchmark"}
                              </div>
                            </div>
                          ) : null}
                          <SearchDropdown
                            value={
                              !!versionCopy?.return_type
                                ? item && indexVersions[item]?.updated_benchmark_code
                                  ? indexVersions[item]?.updated_benchmark_code
                                  : ""
                                : indexVersions[item]?.benchmark_code
                            }
                            input={item}
                            handleBenchmarkChange={(name, value) =>
                              handleBenchmarkChange(item, name, value)
                            }
                            name={
                              !!versionCopy?.return_type
                                ? "updated_benchmark_code"
                                : "benchmark_code"
                            }
                            category={"Benchmark Indices"}
                          />
                        </div>
                        <div className="col-span-1">
                          <CustomDateInput
                            value={
                              !!versionCopy?.return_type
                                ? item && indexVersions[item]?.updated_base_date
                                  ? indexVersions[item].updated_base_date
                                  : indexVersions[item]?.base_date
                                : indexVersions[item]?.base_date
                            }
                            dateOptions={{ dateFormat: "Y-m-d" }}
                            isRequired={false}
                            keyName={
                              !!versionCopy?.return_type
                                ? `updated_base_date_${item}`
                                : `base_date_${item}`
                            }
                            label={`${item} Base Date`}
                            setDate={(selectedDates) => {
                              const dateValue = selectedDates && selectedDates[0]
                                ? selectedDates[0]?.toISOString()
                                : new Date().toISOString();

                              if (!!versionCopy?.return_type) {
                                setIndexVersions((prev) => ({
                                  ...prev,
                                  [item]: {
                                    ...prev[item],
                                    updated_base_date: dateValue,
                                  },
                                }));
                              } else {
                                setIndexVersions((prev) => ({
                                  ...prev,
                                  [item]: {
                                    ...prev[item],
                                    base_date: dateValue,
                                  },
                                }));
                              }
                            }}
                            isVersioned={!!versionCopy?.return_type}
                            versionedValue={
                              versionCopy?.[`${item}_base_date`]
                                ? moment(new Date(versionCopy[`${item}_base_date`])).toISOString()
                                : indexVersions[item]?.base_date
                            }
                            placeholder="Select Date"
                            errors={errors}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-x-4 items-center pt-3 mb-3">
                        <span className="text-slate-500 whitespace-nowrap">{`Sync ${item} value for`}</span>
                        <input
                          type="date"
                          className="form-input w-full"
                          max={moment().format("YYYY-MM-DD")}
                          value={tickerSync[item]?.date || defaultTickerSyncDate()}
                          onChange={(e) =>
                            setTickerSync((prev) => ({
                              ...prev,
                              [item]: { ...prev[item], date: e.target.value },
                            }))
                          }
                        />
                        <button
                          type="button"
                          disabled={tickerSync[item]?.loading || secondsLeft(tickerSync[item]?.until) > 0}
                          title={
                            secondsLeft(tickerSync[item]?.until) > 0
                              ? "Synced too often. Try again when the timer ends."
                              : undefined
                          }
                          onClick={() => handleTickerSync(item)}
                          className="w-full text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {tickerSync[item]?.loading ? (
                            <LoaderComponent />
                          ) : secondsLeft(tickerSync[item]?.until) > 0 ? (
                            `Wait ${formatCountdown(secondsLeft(tickerSync[item]?.until))}`
                          ) : (
                            "Sync Data"
                          )}
                        </button>
                      </div>
                      <IndexDataSource
                        indexCode={indexVersions[item]?.index_code}
                        returnType={item}
                      />
                    </div>
                  ))}

                <SectionTitle title="Sections shown on the page" hint="Tick what the index page should display." />
                {!_.isEmpty(versionCopy) && <CheckCaption text="Currently published" />}
                <div className="grid grid-cols-5 col-span-3 gap-2">
                  {!_.isEmpty(versionCopy)
                    ? checkTypeIndex.map((item) => (
                      <label
                        key={item.label}
                        className="flex items-center space-x-2"
                      >
                        <input
                          type="checkbox"
                          name={item.value}
                          checked={versionCopy[item.value]}
                          onChange={null}
                          disabled
                        />
                        <span className="capitalize">
                          {item.label.replace(/([A-Z])/g, " $1")}
                        </span>
                      </label>
                    ))
                    : null}
                </div>

                {!_.isEmpty(versionCopy) && <CheckCaption text="Your changes" />}
                <div className="grid grid-cols-5 col-span-3 gap-2">
                  {checkTypeIndex.map((key) => (
                    <label
                      key={key.label}
                      className="flex items-center space-x-2"
                    >
                      <input
                        type="checkbox"
                        name={key.value}
                        checked={formData[key.value] || false}
                        onChange={(e) => handleFormData(e)}
                      />
                      <span className="capitalize">
                        {key.label.replace(/([A-Z])/g, " $1")}
                      </span>
                    </label>
                  ))}
                </div>

                <SectionTitle title="Constituents and calculation" />
                <CustomSelect
                  label="Constituents Type"
                  name="constituent_type"
                  value={formData.constituent_type}
                  onChange={(selectedValue) =>
                    handleSelectChange("constituent_type", selectedValue)
                  }
                  options={constituentTypeList}
                  error={errors.constituent_type}
                  required
                  isVersioned={!!versionCopy?.constituent_type}
                  versionedValue={versionCopy?.constituent_type}
                />

                <CustomSelect
                  label="Language"
                  name="languages"
                  value={formData.languages}
                  onChange={(selectedValue) =>
                    handleSelectChange("languages", selectedValue as string[])
                  }
                  options={languageList}
                  error={errors.languages}
                  required
                  isMulti
                  isVersioned={!!versionCopy?.languages}
                  versionedValue={versionCopy?.languages}
                />

                <CustomSelect
                  label="Calculator Type"
                  name="calculator_type"
                  value={formData.calculator_type}
                  onChange={(selectedValue) =>
                    handleSelectChange("calculator_type", selectedValue)
                  }
                  options={calculatorType}
                  error={errors.calculator_type}
                  required
                  isVersioned={!!versionCopy?.calculator_type}
                  versionedValue={versionCopy?.calculator_type}
                />

                {ConstituentTypes.includes(formData?.constituent_type) && (
                  <>
                    <div className="grid grid-cols-5 col-span-3 gap-2">
                      {versionCopy &&
                        constituentType.map((key) => (
                          <label
                            key={key.label}
                            className="flex items-center space-x-2"
                          >
                            <input
                              disabled={true}
                              type="checkbox"
                              name={key.value}
                              checked={formData?.constituent_type?.includes(
                                key.value
                              )}
                            />
                            <span className="capitalize">
                              {key.label.replace(/([A-Z])/g, " $1")}
                            </span>
                          </label>
                        ))}
                    </div>

                    <div className="grid grid-cols-5 col-span-3 gap-2">
                      {constituentType.map((key) => (
                        <label
                          key={key.label}
                          className="flex items-center space-x-2"
                        >
                          <input
                            type="checkbox"
                            name={key.value}
                            checked={constituentData.some(
                              (item) => item === key.value
                            )}
                            onChange={handleConstituentData}
                          />
                          <span className="capitalize">
                            {key.label.replace(/([A-Z])/g, " $1")}
                          </span>
                        </label>
                      ))}
                    </div>
                  </>
                )}

                <SectionTitle title="Methodology and files" hint="Upload the methodology and the sample data files." />
                {formData?.languages?.includes("english") && (
                  <MethodologyFileUploader
                    key="english"
                    buttonText="English"
                    handleUpdate={(data) =>
                      handleUpdate(data, "methodology_media_url")
                    }
                    language="english"
                    keyValue="methodology_media_url"
                    versionCopy={versionCopy}
                    isDocument={isDocument}
                    formData={formData}
                    selectedFiles={selectedFiles}
                    setDocumentView={setDocumentView}
                  />
                )}

                {formData?.languages?.includes("portuguese") && (
                  <MethodologyFileUploader
                    key="portuguese"
                    buttonText="Portuguese "
                    handleUpdate={(data) =>
                      handleUpdate(data, "portuguese_media_url")
                    }
                    language="portuguese"
                    keyValue="portuguese_media_url"
                    versionCopy={versionCopy}
                    isDocument={isDocument}
                    formData={formData}
                    selectedFiles={selectedFiles}
                    setDocumentView={setDocumentView}
                  />
                )}

                {formData?.languages?.includes("korean") && (
                  <MethodologyFileUploader
                    key="korean"
                    buttonText="Korean "
                    handleUpdate={(data) =>
                      handleUpdate(data, "korean_media_url")
                    }
                    language="korean"
                    keyValue="korean_media_url"
                    versionCopy={versionCopy}
                    isDocument={isDocument}
                    formData={formData}
                    selectedFiles={selectedFiles}
                    setDocumentView={setDocumentView}
                  />
                )}

                {formData?.languages?.includes("hebrew") && (
                  <MethodologyFileUploader
                    key="hebrew"
                    buttonText="Hebrew "
                    handleUpdate={(data) =>
                      handleUpdate(data, "hebrew_media_url")
                    }
                    language="hebrew"
                    keyValue="hebrew_media_url"
                    versionCopy={versionCopy}
                    isDocument={isDocument}
                    formData={formData}
                    selectedFiles={selectedFiles}
                    setDocumentView={setDocumentView}
                  />
                )}

                {formData?.languages?.includes("japanese") && (
                  <MethodologyFileUploader
                    key="japanese"
                    buttonText="Japanese "
                    handleUpdate={(data) =>
                      handleUpdate(data, "japanese_media_url")
                    }
                    language="japanese"
                    keyValue="japanese_media_url"
                    versionCopy={versionCopy}
                    isDocument={isDocument}
                    formData={formData}
                    selectedFiles={selectedFiles}
                    setDocumentView={setDocumentView}
                  />
                )}

                {formData?.languages?.includes("english") &&
                  formData?.languages?.includes("portuguese") &&
                  formData?.languages?.includes("japanese") &&
                  formData?.languages?.includes("hebrew") &&
                  formData?.languages?.includes("korean") && (
                    <div className="col-span-1"></div>
                  )}

                <div className="grid grid-cols-3 col-span-3 gap-2">
                  <div className="col-span-1">
                    <div className="flex gap-2">
                      <FileUpload
                        label="Index Values"
                        name="indexValues"
                        id={id}
                        files={files}
                        setFiles={setFiles}
                        errors={errors}
                        setErrors={setErrors}
                        allowedTypes={["text/csv"]}
                      />
                    </div>
                  </div>
                  <div className="col-span-1">
                    <div className="flex gap-2">
                      <FileUpload
                        label="Constituents"
                        name="constituentsFile"
                        files={files}
                        id={id}
                        setFiles={setFiles}
                        errors={errors}
                        setErrors={setErrors}
                        allowedTypes={["text/csv"]}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 col-span-3 gap-2">
                  <div className="col-span-2 ">
                    <div className="flex">
                      <div
                        onClick={() => handleReturnTypeValueDownload("pr", id)}
                        className="flex items-center px-4 py-2 m-2 ml-0 space-x-2 border border-gray-300 rounded-md hover:bg-gray-100"
                      >
                        ⬇️ PR Code Value
                      </div>

                      <div
                        onClick={() => handleReturnTypeValueDownload("tr", id)}
                        className="flex items-center px-4 py-2 m-2 space-x-2 border border-gray-300 rounded-md hover:bg-gray-100"
                      >
                        ⬇️ TR Code Value
                      </div>

                      <div
                        onClick={() => handleReturnTypeValueDownload("ntr", id)}
                        className="flex items-center px-4 py-2 m-2 space-x-2 border border-gray-300 rounded-md hover:bg-gray-100"
                      >
                        ⬇️ NTR Code Value
                      </div>
                    </div>
                    <div
                      onClick={handleValueDownload}
                      className="flex items-center px-4 py-2 w-[15%] border border-gray-300 rounded-md hover:bg-gray-100 cursor-pointer"
                    >
                      ⬇️ All Value
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 z-10 flex justify-end col-span-3 gap-3 py-3 mt-6 bg-white border-t border-slate-200 dark:bg-zink-700 dark:border-zink-500">
                  <button
                    type="button"
                    className="btn bg-white border-slate-200 text-slate-600 hover:bg-slate-100 dark:bg-transparent dark:border-zink-500 dark:text-zink-100"
                    onClick={() => {
                      setFormData(initialFormData);
                      setFiles({
                        factsheet: null,
                        indexValues: null,
                        constituentsFile: null,
                        englishMethodologyFile: null,
                        portugeseMethodologyFile: null,
                      });
                      setErrors({});
                      setCheckedType([]);
                      setConstituentData([]);
                      setLoader(false);
                    }}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    disabled={loader}
                    className="min-w-[130px] justify-center text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600"
                    onClick={handleSubmit}
                  >
                    {loader ? <LoaderComponent /> : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-x-5">
          <div className="col-span-12 card 2xl:col-span-12">
            <div className="card-body">
              <div className="flex items-center">
                <h6 className="text-15 grow">Index Constituents</h6>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full whitespace-nowrap">
                  <thead className="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:text-zink-200 dark:bg-zink-600">
                    <tr>
                      <th className="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500">
                        Symbol
                      </th>
                      <th className="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500">
                        Name
                      </th>
                      <th className="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500">
                        ISIN
                      </th>
                      <th className="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500">
                        Weight
                      </th>
                      <th className="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {indexConstituentList.map(
                      (constituent: any, index: number) => (
                        <tr key={index}>
                          <td className="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500">
                            {constituent.mic}
                          </td>
                          <td className="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500">
                            {constituent.constituent_name}
                          </td>
                          <td className="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500">
                            {constituent.isin}
                          </td>
                          <td className="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500">
                            {constituent.weight}
                          </td>
                          <td className="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500">
                            {moment(constituent.cdate).format("DD-MM-YYYY")}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <SourceVerification
          tickers={(formData?.return_type || [])
            .map((rt: string) => ({ returnType: rt, indexCode: indexVersions[rt]?.index_code }))
            .filter((t: any) => t.indexCode)}
        />

        <div className="grid grid-cols-12 gap-x-5">
          <div className="col-span-12 card 2xl:col-span-12">
            <div className="card-body">
              <div className="flex items-center">
                <h6 className="text-15 grow">Index Upload List</h6>
              </div>
            </div>

            <div className="card-body">
              <TableContainer
                isPagination={true}
                columns={indexColumns || []}
                data={indexUploads || []}
                pageCount={pageCount}
                pagination={pagination}
                totalItems={totalItems}
                onPaginationChange={(updater: any) => {
                  const nextState = updater(pagination);
                  if (nextState.pageIndex !== pagination.pageIndex)
                    setPagination(nextState);
                }}
                divclassName="overflow-x-auto"
                tableclassName="w-full whitespace-nowrap"
                theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:text-zink-200 dark:bg-zink-600"
                thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500"
                tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
                PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
              />
            </div>
          </div>
        </div>

        {showMeta ? (
          <Drawer
            show={showMeta}
            onHide={toggleMeta}
            id="drawerEnd"
            drawer-end="true"
            className="fixed inset-y-0 flex flex-col w-1/2 transition-transform duration-300 ease-in-out transform bg-white shadow ltr:right-0 rtl:left-0 z-drawer dark:bg-zink-600"
          >
            <Drawer.Header
              className="flex items-center justify-between p-4 border-b card-body border-slate-200 dark:border-zink-500"
              closeButtonClass="lucide lucide-x size-4 transition-all duration-200 ease-linear text-slate-500 hover:text-slate-700 dark:text-zink-200 dark:hover:text-zink-50"
            >
              <Drawer.Title className="text-15">{"Edit"}</Drawer.Title>
              <X className="transition-all duration-200 ease-linear size-4 text-slate-500 hover:text-slate-700 dark:text-zink-200 dark:hover:text-zink-50"></X>
            </Drawer.Header>
            <Drawer.Body className="h-full p-4 overflow-y-auto">
              <MetaData
                handleMetaChange={handleMetaChange}
                modelName="index"
                metaEdit={metaEdit}
                showError={showError}
                toggleMeta={toggleMeta}
                handleMetaData={handleMetaData}
                loader={loader}
              />
            </Drawer.Body>
          </Drawer>
        ) : null}

        <Drawer
          show={showImage}
          onHide={toggleImage}
          id="drawerEnd"
          drawer-end="true"
          className="fixed inset-y-0 flex flex-col w-1/2 transition-transform duration-300 ease-in-out transform bg-white shadow ltr:right-0 rtl:left-0 z-drawer dark:bg-zink-600"
        >
          <Drawer.Header
            className="flex items-center justify-between p-4 border-b card-body border-slate-200 dark:border-zink-500"
            closeButtonClass="lucide lucide-x size-4 transition-all duration-200 ease-linear text-slate-500 hover:text-slate-700 dark:text-zink-200 dark:hover:text-zink-50"
          >
            <Drawer.Title className="text-15">{"Select Image"}</Drawer.Title>
            <X className="transition-all duration-200 ease-linear size-4 text-slate-500 hover:text-slate-700 dark:text-zink-200 dark:hover:text-zink-50"></X>
          </Drawer.Header>
          <Drawer.Body className="h-full p-4 overflow-y-auto">
            <ImageView
              showImage={showImage}
              setEventData={setImageData}
              togleImage={toggleImage}
            />
          </Drawer.Body>
        </Drawer>

        <Drawer
          show={showMode}
          onHide={defaultToggle}
          id="drawerEnd"
          drawer-end="true"
          className="fixed inset-y-0 flex flex-col w-1/2 transition-transform duration-300 ease-in-out transform bg-white shadow ltr:right-0 rtl:left-0 z-drawer dark:bg-zink-600"
        >
          <Drawer.Header
            className="flex items-center justify-between p-4 border-b card-body border-slate-200 dark:border-zink-500"
            closeButtonClass="lucide lucide-x size-4 transition-all duration-200 ease-linear text-slate-500 hover:text-slate-700 dark:text-zink-200 dark:hover:text-zink-50"
          >
            <Drawer.Title className="text-15">
              {documentView?.file_name
                ? documentView?.file_name
                : "Document View"}
            </Drawer.Title>
            <X className="transition-all duration-200 ease-linear size-4 text-slate-500 hover:text-slate-700 dark:text-zink-200 dark:hover:text-zink-50"></X>
          </Drawer.Header>
          <Drawer.Body className="h-full p-4 overflow-y-auto">
            <iframe
              src={
                docView === undefined
                  ? getImageUrl(
                    documentView?.document ? documentView?.document : null
                  )
                  : documentView?.document
                    ? documentView?.document
                    : null
              }
              className="w-[80vh] h-[80vh] rounded-md"
            />
          </Drawer.Body>
        </Drawer>
      </React.Fragment>
    </>
  );
};

export default ManageIndices;